import React, { useId } from 'react';
import PropTypes from 'prop-types';
import { Check } from 'lucide-react';
import './Checkbox.css';

/**
 * Checkbox — Design System
 *
 * Multi-select checkbox option with a square selector, a title and an
 * optional description. Wraps a native `<input type="checkbox">` (visually
 * hidden) so it inherits keyboard/form semantics.
 *
 * Two sizes (separate Figma components):
 *   - large  → 32px selector, title `typo-h4`, description `typo-body`.
 *              Figma 4109:324
 *   - medium → 24px selector, title `typo-h5`, description `typo-body-sm`.
 *              Figma 4109:376
 *
 * Both sizes share the success colour scheme: hover turns the border and
 * title `--color-ui-success`; checked fills the selector with success green
 * and reveals a white lucide `Check` icon.
 */
const Checkbox = ({
  size = 'medium',
  title,
  description = null,
  checked = false,
  disabled = false,
  error = '',
  name,
  value,
  onChange,
  className = '',
  ...props
}) => {
  const errorId = `${useId()}-error`;
  const titleClass = size === 'large' ? 'typo-h4' : 'typo-h5';
  const descriptionClass = size === 'large' ? 'typo-body' : 'typo-body-sm';

  const rootClass = [
    'ds-checkbox',
    `ds-checkbox--${size}`,
    disabled ? 'ds-checkbox--disabled' : '',
    error ? 'ds-checkbox--error' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <label className={rootClass}>
      <input
        type="checkbox"
        className="ds-checkbox__input"
        name={name}
        value={value}
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={error ? errorId : undefined}
        {...props}
      />
      <span className="ds-checkbox__main">
        <span className="ds-checkbox__selector" aria-hidden="true">
          <Check className="ds-checkbox__check" />
        </span>
        {title != null && (
          <span className={`ds-checkbox__title ${titleClass}`}>{title}</span>
        )}
      </span>
      {description != null && (
        <span className={`ds-checkbox__description ${descriptionClass}`}>
          {description}
        </span>
      )}
      {error && (
        <span id={errorId} className="ds-checkbox__error typo-body-sm" role="alert">
          {error}
        </span>
      )}
    </label>
  );
};

Checkbox.propTypes = {
  size: PropTypes.oneOf(['large', 'medium']),
  title: PropTypes.node,
  description: PropTypes.node,
  checked: PropTypes.bool,
  disabled: PropTypes.bool,
  /** Validation message under the box — sets aria-invalid and links it with aria-describedby */
  error: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  className: PropTypes.string,
};

export default Checkbox;
